import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { BellOff } from "lucide-react";
import axios from "axios";
import useUserRole from "../../app/auth/useUserRole";
import { getToken } from "../../app/auth/authConfig";

const NotificationSubscriberManagement = forwardRef((props, ref) => {
  const [subscribers, setSubscribers] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unsubscribingIds, setUnsubscribingIds] = useState([]);
  const currentUserRole = useUserRole();

  useEffect(() => {
    fetchSubscribers();
  }, []);

  useImperativeHandle(ref, () => ({
    fetchSubscribers,
  }));

  const fetchSubscribers = async () => {
    try {
      setIsLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/subscribers`;
      const token = await getToken();

      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      
      setSubscribers(res.data.subscribers || []);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSubscribers = subscribers.filter((s) =>
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubscribers.length / perPage);
  const paginatedSubscribers = filteredSubscribers.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const unsubscribeSubscriber = async (id) => {
    try {
      setUnsubscribingIds((prev) => [...prev, id]);

      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/notifications/subscribers/${id}`;
      const token = await getToken();

      await axios.delete(baseURL, {
        headers: { "oidc-token": token },
      });
      
      setSubscribers((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      console.error("Unsubscribe error", err);
      alert("Failed to unsubscribe subscriber");
    } finally {
      setUnsubscribingIds((prev) => prev.filter((value) => value !== id));
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        <input
          type="text"
          placeholder="Search by email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          disabled={isLoading}
          style={{
            padding: "10px 14px",
            flex: 1,
            borderRadius: "6px",
            border: "1px solid #ddd",
            backgroundColor: isLoading ? "#f0f0f0" : "white",
            cursor: isLoading ? "not-allowed" : "auto",
            fontSize: "14px",
          }}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
        <strong style={{ fontSize: "14px", color: "#666" }}>
          Total Subscribers: {filteredSubscribers.length}
        </strong>
        <div>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={isLoading || page === 1}
            style={{
              cursor: isLoading || page === 1 ? "not-allowed" : "pointer",
              opacity: isLoading || page === 1 ? 0.6 : 1,
              marginRight: "8px",
              padding: "8px 12px",
              background: "#f0f0f0",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            Prev
          </button>
          <span style={{ margin: "0 8px", fontSize: "12px" }}>
            Page {page} of {Math.max(1, totalPages)}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={isLoading || page === totalPages}
            style={{
              cursor: isLoading || page === totalPages ? "not-allowed" : "pointer",
              opacity: isLoading || page === totalPages ? 0.6 : 1,
              marginLeft: "8px",
              padding: "8px 12px",
              background: "#f0f0f0",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            Next
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 8px",
            fontSize: "14px",
            marginTop: 16,
          }}
        >
          <thead>
            <tr style={{ height: "50px" }}>
              <th
                style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  borderRadius: "8px 0 0 8px",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                📧 Email
              </th>
              <th
                style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "left",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                📅 Subscribed Date
              </th>
              <th
                style={{
                  background: "#bb1ccc",
                  color: "white",
                  fontWeight: "600",
                  padding: "14px 16px",
                  textAlign: "center",
                  borderRadius: "0 8px 8px 0",
                  fontSize: "13px",
                  letterSpacing: "0.5px",
                }}
              >
                ⚙️ Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: perPage }).map((_, i) => (
                <tr
                  key={`sk-${i}`}
                  style={{
                    background: i % 2 === 0 ? "#ffffff" : "#f3f0f7",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    border: "1px solid #e5e5e5",
                  }}
                >
                  <td style={{ padding: "14px 16px", borderRadius: "8px 0 0 8px" }}>
                    <div className="h-4 bg-gray-300 rounded w-40 animate-pulse" />
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div className="h-4 bg-gray-300 rounded w-32 animate-pulse" />
                  </td>
                  <td style={{ padding: "14px 16px", textAlign: "center", borderRadius: "0 8px 8px 0" }}>
                    <div className="flex gap-2 justify-center">
                      <div className="h-8 bg-gray-300 rounded w-28 animate-pulse" />
                    </div>
                  </td>
                </tr>
              ))
            ) : filteredSubscribers.length === 0 ? (
              <tr>
                <td
                  colSpan="3"
                  style={{
                    padding: "40px 16px",
                    textAlign: "center",
                    background: "#f8f8f8",
                    borderRadius: "8px",
                  }}
                >
                  <span style={{ color: "#999", fontSize: "15px" }}>
                    📭 No subscribers found
                  </span>
                </td>
              </tr>
            ) : (
              paginatedSubscribers.map((sub, idx) => (
                <tr
                  key={sub.id}
                  style={{
                    background: idx % 2 === 0 ? "#ffffff" : "#f3f0f7",
                    borderRadius: "8px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    border: "1px solid #e5e5e5",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 4px 12px rgba(187,28,204,0.15)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow =
                      "0 1px 3px rgba(0,0,0,0.06)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <td
                    style={{
                      padding: "14px 16px",
                      borderRadius: "8px 0 0 8px",
                      fontWeight: "500",
                      color: "#333",
                    }}
                  >
                    {sub.email}
                  </td>
                  <td style={{ padding: "14px 16px", color: "#666" }}>
                    {formatDate(sub.subscribedAt)}
                  </td>
                  <td
                    style={{
                      padding: "14px 16px",
                      borderRadius: "0 8px 8px 0",
                      textAlign: "center",
                    }}
                  >
                    <div className="flex gap-2 justify-center">
                      {(() => {
                        const isUnsubscribing = unsubscribingIds.includes(sub.id);

                        return (
                      <button
                        onClick={() => unsubscribeSubscriber(sub.id)}
                        disabled={isUnsubscribing || currentUserRole !== "Admin"}
                        title={currentUserRole !== "Admin" ? "Only admins can manage subscribers" : "Unsubscribe"}
                        style={{
                          background: currentUserRole === "Admin" ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" : "#ccc",
                          color: "white",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: "6px",
                          cursor: isUnsubscribing || currentUserRole !== "Admin" ? "not-allowed" : "pointer",
                          opacity: isUnsubscribing || currentUserRole !== "Admin" ? 0.6 : 1,
                          fontSize: "12px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (isUnsubscribing || currentUserRole !== "Admin") return;
                          e.currentTarget.style.transform = "scale(1.05)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 8px rgba(239,68,68,0.3)";
                        }}
                        onMouseLeave={(e) => {
                          if (isUnsubscribing || currentUserRole !== "Admin") return;
                          e.currentTarget.style.transform = "scale(1)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <BellOff width={14} /> {isUnsubscribing ? "Unsubscribing..." : "Unsubscribe"}
                      </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

NotificationSubscriberManagement.displayName = "NotificationSubscriberManagement";

export default NotificationSubscriberManagement;
